import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';

import Tooltip from '@/components/ui-bridge/core/Tooltip';
import { format, parse, parseISO, differenceInMinutes, isBefore, set } from 'date-fns';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import orderCardIcon from '@/components/SchedulePage/OrderCard/orderCardIcon';
import classes from '@/components/SchedulePage/OrderCard/style.module.scss';
import getIcon from '@/helpers/order-icon';
import flexibleTime from '@/images/icons/flexibleTime.svg';
import keysDelayed from '@/images/icons/keysDelayed.svg';
import iconOrderDelayed15 from '@/images/icons/orderDelayed15.svg';
import iconOrderDelayed30 from '@/images/icons/orderDelayed30.svg';
import isUnavailableExclamation from '@/images/icons/scheduleCard/is-unavailable-exclamation.svg';

const orderStatusToShow = {
  WAITING: {
    background: '#ffffff',
    border: '#005f98',
  },
  ACCEPTED: {
    background: '#ffffff',
    border: '#005f98',
  },
  ASSIGNED: {
    background: '#0170b3',
    border: '#005f98',
  },
  FINALIZED_BY_DEVICE: {
    background: '#ffffff',
    border: '#3DC032',
  },
  FINISHED: {
    background: '#74ff68',
    border: '#3DC032',
  },
  IN_PROGRESS: {
    background: '#F2DE28',
    border: '#F2C628',
  },
  VNR: {
    background: '#ffffff',
    border: '#C03030',
  },
  REJECTED: {
    background: '#F74343',
    border: '#C03030',
  },
  CANCELED: {
    background: '#F74343',
    border: '#C03030',
  },
};

const OrderCardComponent = ({
  intl,
  order,
  schedule,
  setSelectedOrder,
  insideTimelineStyle,
  onDragStart,
  onDragEnd,
  onDragEndCapture,
  isAlreadyBeingEditedByAnotherUser,
  isUnavailable,
  inTimeline,
  lockIconState,
  keyIconState,
  formattedAllowedAtDate,
  formattedCreatedAtDate,
  formattedOrderAddress,
  forwardedRef,
  isMapSelected,
  onMapOrderClick,
}) => {
  const [orderStatus, setOrderStatus] = useState(order?.status);
  const [orderDelayed30, setOrderDelayed30] = useState(false);
  const [orderDelayed15, setOrderDelayed15] = useState(false);
  const [hasOrderActions, setHasOrderActions] = useState(false);
  const [keysNotReturned, setKeysNotReturned] = useState(false);
  const isFlexibleTime = inTimeline && schedule?.flextime === true;
  const hasBadges = orderDelayed15 || orderDelayed30 || keysNotReturned || isFlexibleTime;

  useEffect(() => {
    setOrderStatus(order?.status);
  }, [order?.status]);

  const renderDelayedIcon = (icon, title, threshold) => (
    <Tooltip
      title={intl.formatMessage({ id: 'ORDER_DELAYED_TOOLTIP' }, { threshold })}
      placement="bottom-start"
      slotProps={{
        tooltip: { className: classes.orderCardTooltip }
      }}
    >
      <img className={classes.orderCardBadge} src={icon} alt={title} />
    </Tooltip>
  );

  const renderKeysDelayedIcon = () => (
    <Tooltip
      title={intl.formatMessage({ id: 'KEYS_NOT_RETURNED_TOOLTIP' })}
      placement="bottom-start"
      slotProps={{
        tooltip: { className: classes.orderCardTooltip }
      }}
    >
      <img
        className={classes.orderCardBadge}
        src={keysDelayed}
        alt={intl.formatMessage({ id: 'KEYS_NOT_RETURNED_TOOLTIP' })}
      />
    </Tooltip>
  );

  const renderFlexibleTimeBadge = () => (
    <Tooltip
      title={intl.formatMessage({ id: 'FLEXTIME_BADGE_TOOLTIP' })}
      placement="top-start"
      slotProps={{
        tooltip: { className: classes.orderCardTooltip }
      }}
    >
      <img
        className={classes.orderCardBadge}
        src={flexibleTime}
        alt={intl.formatMessage({ id: 'FLEXTIME_BADGE_TOOLTIP' })}
      />
    </Tooltip>
  );

  useEffect(() => {
    const checkTime30 = time => {
      const now = new Date();
      const timeParsed = parse(time, 'HH:mm', new Date());
      const minutesDiff = differenceInMinutes(timeParsed, now);
      return minutesDiff <= 30;
    };

    const checkTime15 = time => {
      const now = new Date();
      const timeParsed = parse(time, 'HH:mm', new Date());
      const minutesDiff = differenceInMinutes(timeParsed, now);
      return minutesDiff <= 15;
    };

    const accepted = ['IN_PROGRESS', 'ACCEPTED', 'ASSIGNED'];

    const dateNow = format(new Date(), 'yyyy-MM-dd');

    const checkKeysReturned = async () => {
      if (!['FINALIZED_BY_DEVICE', 'FINISHED'].includes(orderStatus)) {
        setKeysNotReturned(false);
        return;
      }

      const currentTime = new Date();
      const cutoffTime = set(new Date(), { hour: 16, minute: 30 });

      const scheduleDate = schedule?.when?.date;
      if (!scheduleDate) {
        setKeysNotReturned(false);
        return;
      }

      const isBeforeScheduledDate = isBefore(parseISO(scheduleDate), parseISO(dateNow));

      if (!isBeforeScheduledDate && isBefore(currentTime, cutoffTime)) {
        setKeysNotReturned(false);
        return;
      }

      const accessInfo = order.accessInformation?.accessInformation;
      const keysDevoultionInPlace =
        (accessInfo?.type === 'No local' && accessInfo?.keysDevolutionType === 'Mesmo local da retirada') ||
        accessInfo?.keysDevolutionType === 'No local';

      if (keysDevoultionInPlace) {
        setKeysNotReturned(false);
      } else {
        try {
          const { data } = await API.get(
            `/activity-log/orders/${order.public_id}/actions/exist?types[]=INSPECTOR_RETURN_KEYS`
          );
          setKeysNotReturned(!data?.exists);
        } catch (error) {
          console.error('Erro ao verificar devolução de chaves:', error);
          setKeysNotReturned(false);
        }
      }
    };

    const checkOrder = async () => {
      if (inTimeline) {
        if (Math.random() < 0.1 && orderStatus !== 'FINISHED') {
          //1/10
          const { data: dataStatus } = await API.get(`/orders/${order.public_id}/status`);
          order.status = dataStatus.status;
          setOrderStatus(dataStatus.status);
        }

        if (
          accepted.includes(orderStatus) &&
          schedule?.flextime !== true &&
          checkTime30(schedule?.when?.start_time) &&
          !hasOrderActions &&
          dateNow === schedule?.when?.date
        ) {
          const { data } = await API.get(
            `/activity-log/orders/${order.public_id}/actions/exist?types[]=INSPECTOR_CHECKIN&types[]=INSPECTOR_DO_INSPECTION_NOW`
          );

          if (data?.exists) {
            setHasOrderActions(true);
            setOrderDelayed30(false);
            setOrderDelayed15(false);
          } else {
            const isDelayed15 = checkTime15(schedule?.when?.start_time);
            setOrderDelayed30(!isDelayed15);
            setOrderDelayed15(isDelayed15);
          }
        } else {
          await checkKeysReturned();
          setOrderDelayed30(false);
          setOrderDelayed15(false);
        }
      } else {
        setOrderDelayed30(false);
        setOrderDelayed15(false);
        setKeysNotReturned(false);
      }
    };

    const scheduleDate = schedule?.when?.date;

    const toDate = value => (typeof value === 'string' ? parseISO(value) : value);

    if (dateNow === scheduleDate || isBefore(toDate(scheduleDate), toDate(dateNow))) {
      checkOrder();

      const intervalId = setInterval(() => {
        checkOrder();
      }, 90000);
      return () => clearInterval(intervalId);
    }
  }, [inTimeline, order, orderStatus, hasOrderActions, setHasOrderActions]);

  return (
    <button
      type="button"
      className={`
        ${classes.orderCard}
        ${inTimeline ? classes.inTimeline : ''}
        ${orderDelayed15 ? classes.orderDelayed15 : ''}
        ${orderDelayed30 ? classes.orderDelayed30 : ''}
        ${keysNotReturned ? classes.keysNotReturned : ''}
        ${isAlreadyBeingEditedByAnotherUser ? classes.isEditingThisOrder : ''}
        ${isUnavailable ? classes.isUnavailable : ''}
        ${isFlexibleTime ? classes.flexibleTime : ''}
        ${isMapSelected ? classes.isMapSelected : ''}
      `}
      style={insideTimelineStyle}
      onClick={() =>
        onMapOrderClick ? onMapOrderClick(order) : setSelectedOrder({ orderId: order?.public_id || order?.id })
      }
      onDragEndCapture={onDragEndCapture}
      onDragStart={evt => (onMapOrderClick ? evt.preventDefault() : onDragStart({ order, schedule }, evt))}
      onDragEnd={onDragEnd}
      ref={forwardedRef}
      draggable={!onMapOrderClick}
    >
      {hasBadges && (
        <div className={classes.orderCardBadges}>
          {orderDelayed15 && renderDelayedIcon(iconOrderDelayed15, intl.formatMessage({ id: 'iconOrderDelayed15' }), 15)}
          {orderDelayed30 && renderDelayedIcon(iconOrderDelayed30, intl.formatMessage({ id: 'iconOrderDelayed30' }), 30)}
          {keysNotReturned && renderKeysDelayedIcon()}
          {isFlexibleTime && renderFlexibleTimeBadge()}
        </div>
      )}

      <div className={`${classes.line} ${classes.titleLine} ${inTimeline ? classes.inTimeline : ''}`}>
        {isUnavailable && inTimeline && (
          <Tooltip
            title={intl.formatMessage({ id: 'INSPECTOR_UNAVAILABILITY_BADGE_WARNING' })}
            placement="top-start"
            slotProps={{
              tooltip: { className: classes.orderCardTooltip }
            }}
          >
            <img className={classes.isUnavailableBadge} src={isUnavailableExclamation} alt="Is unavailable icon" />
          </Tooltip>
        )}

        <Tooltip
          title={intl.formatMessage({ id: order?.type })}
          placement="bottom-end"
          slotProps={{
            tooltip: { className: classes.orderCardTooltip }
          }}
        >
          <div className={classes.icon}>
            <img
              src={getIcon(order?.type)}
              alt={intl.formatMessage({ id: order?.type })}
              title={intl.formatMessage({ id: order?.type })}
            />
          </div>
        </Tooltip>

        <h4 className={classes.orderIdentifier} title={order?.identifier}>
          {order?.identifier}
        </h4>

        {!inTimeline && order?.credits && (
          <Tooltip
            title={intl.formatMessage({ id: 'Financial.credits' })}
            placement="right"
            slotProps={{
              tooltip: { className: classes.orderCardTooltip }
            }}
          >
            <span className={classes.orderFootage}>
              {((order?.credits || 0) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </Tooltip>
        )}
      </div>

      {!inTimeline ? (
        <>
          <div className={`${classes.line} ${classes.titleLine}`}>
            <div className={classes.orderCode}>
              <p>{order?.code}</p>

              <div className={classes.separator}>&#x2022;</div>

              <p>{intl.formatMessage({ id: order?.building_type })}</p>
            </div>
          </div>

          <div className={`${classes.line} ${classes.titleLine}`}>
            <p className={`${classes.orderName} ${classes.truncate}`}>{order?.client}</p>
          </div>

          <Tooltip
            title={formattedOrderAddress}
            placement="bottom-start"
            slotProps={{
              tooltip: { className: classes.orderCardTooltip }
            }}
          >
            <div className={classes.line}>
              <div className={classes.orderAddress}>
                <p className={`${classes.neighborhoodName} ${classes.truncate}`}>{order?.neighborhood} </p>

                <div className={classes.separator}>&#x2022;</div>

                <p className={`${classes.city} ${classes.truncate}`}>{order?.city}</p>
              </div>
            </div>
          </Tooltip>
        </>
      ) : (
        <>
          <div className={`${classes.line} ${classes.titleLine}`}>
            <p className={`${classes.orderName} ${classes.truncate}`}>{order?.client}</p>
          </div>

          <div className={`${classes.line} ${classes.titleLine}`}>
            <div className={`${classes.orderCode} ${classes.truncate}`}>
              <p>{order?.code}</p>

              <div className={classes.separator}>&#x2022;</div>

              <p>
                {((order?.credits || 0) / 100).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>

          <Tooltip
            title={formattedOrderAddress}
            placement="bottom-start"
            slotProps={{
              tooltip: { className: classes.orderCardTooltip }
            }}
          >
            <div className={classes.line}>
              <div className={`${classes.orderAddress} ${classes.truncate}`}>
                <p className={`${classes.neighborhoodName} ${classes.truncate}`}>{order?.neighborhood}</p>
              </div>
            </div>
          </Tooltip>

          {orderStatus in orderStatusToShow && (
            <div className={classes.line}>
              <div className={classes.orderStatus}>
                <div
                  className={classes.statusDot}
                  style={{
                    background: orderStatusToShow[orderStatus].background,
                    borderColor: orderStatusToShow[orderStatus].border,
                  }}
                />

                <p className={classes.statusName}>{intl.formatMessage({ id: orderStatus })}</p>
              </div>
            </div>
          )}
        </>
      )}

      {!inTimeline && (
        <div className={classes.line}>
          <div className={classes.orderIcons}>
            {lockIconState === null ? (
              <Tooltip
                title={intl.formatMessage({ id: 'NO_RELEASE_DATE_SPECIFIED' })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('unlocked')} alt="Lock icon" />
              </Tooltip>
            ) : lockIconState ? (
              <Tooltip
                title={`${intl.formatMessage({ id: 'RELEASED_ON' })} ${formattedAllowedAtDate || ''}`}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('unlocked')} alt="Lock icon" />
              </Tooltip>
            ) : (
              <Tooltip
                title={`${intl.formatMessage({ id: 'RELEASES_IN' })} ${formattedAllowedAtDate || ''}`}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('locked')} alt="Lock icon" />
              </Tooltip>
            )}

            {order?.furnished && (
              <Tooltip
                title={intl.formatMessage({ id: order?.furnished })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon(order?.furnished)} alt="House icon" />
              </Tooltip>
            )}

            {!!order?.accompanied_inspection && (
              <Tooltip
                title={intl.formatMessage({
                  id: order?.accompanied_inspection?.isAccompanied
                    ? 'ACCOMPANIED_INSPECTION'
                    : 'NON_ACCOMPANIED_INSPECTION',
                })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img
                  src={
                    order?.accompanied_inspection?.isAccompanied
                      ? orderCardIcon('people')
                      : orderCardIcon('people-disabled')
                  }
                  alt="People icon"
                />
              </Tooltip>
            )}

            {order?.modality === 'premium' ? (
              <Tooltip
                title={intl.formatMessage({ id: 'PREMIUM_SCHEDULE_INSPECTION' })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('star')} alt="Star icon" />
              </Tooltip>
            ) : (
              <img src={orderCardIcon('star-disabled')} alt="Star icon" />
            )}

            {order?.urgency === 'express' ? (
              <Tooltip
                title={intl.formatMessage({ id: 'EXPRESS_SCHEDULE_INSPECTION' })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('lightning')} alt="Lightning icon" />
              </Tooltip>
            ) : (
              <img src={orderCardIcon('lightning-disabled')} alt="Lightning icon" />
            )}

            {keyIconState === 'ACCESS_INFORMATION_LOCAL_KEYS_TYPE' ? (
              <Tooltip
                title={intl.formatMessage({ id: `SCHEDULE.${order?.access_information?.type}` })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'PROPERTY_ACCESS_LOCAL_KEYS_TYPE' ? (
              <Tooltip
                title={intl.formatMessage({ id: `SCHEDULE.${order?.property_access?.type}` })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'ACCESS_INFORMATION_REAL_ESTATE_KEYS_TYPE' ? (
              <Tooltip
                title={intl.formatMessage(
                  { id: `SCHEDULE.${order?.access_information?.type}` },
                  { neighborhood: order?.access_information?.address?.neighborhood }
                )}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'PROPERTY_ACCESS_REAL_ESTATE_KEYS_TYPE' ? (
              <Tooltip
                title={intl.formatMessage(
                  { id: `SCHEDULE.${order?.property_access?.type}` },
                  { neighborhood: order?.property_access?.address?.neighborhood }
                )}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'ACCESS_INFORMATION_OTHER_KEYS_TYPE' ? (
              <Tooltip
                title={order?.access_information?.address?.neighborhood}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'PROPERTY_ACCESS_OTHER_KEYS_TYPE' ? (
              <Tooltip
                title={order?.property_access?.address?.neighborhood}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'ACCESS_INFORMATION_KEYS_LOCATION_KEYS_TYPE' ? (
              <Tooltip
                title={order?.access_information?.keysLocation}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : keyIconState === 'PROPERTY_ACCESS_DETAILS_KEYS_TYPE' ? (
              <Tooltip
                title={order?.property_access?.keysLocation}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('key')} alt="Key icon" />
              </Tooltip>
            ) : (
              <img src={orderCardIcon('key')} alt="Key icon" />
            )}

            {order?.details || order?.access_information?.instructions ? (
              <Tooltip
                title={intl.formatMessage({
                  id: order?.details ? 'CHECK_ORDER_DETAILS' : 'CHECK_ORDER_ACCESS_INFORMATION',
                })}
                placement="bottom"
                slotProps={{
                  tooltip: { className: classes.orderCardTooltip }
                }}
              >
                <img src={orderCardIcon('warning')} alt="Warning icon" />
              </Tooltip>
            ) : (
              <img src={orderCardIcon('warning-disabled')} alt="Warning icon" />
            )}
          </div>

          <span className={classes.orderDate}>{formattedCreatedAtDate}</span>
        </div>
      )}
    </button>
  );
};

OrderCardComponent.propTypes = {
  order: PropTypes.object.isRequired,
  schedule: PropTypes.object,
  setSelectedOrder: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  insideTimelineStyle: PropTypes.object,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragEndCapture: PropTypes.func.isRequired,
  isAlreadyBeingEditedByAnotherUser: PropTypes.bool.isRequired,
  isUnavailable: PropTypes.bool.isRequired,
  inTimeline: PropTypes.bool,
  lockIconState: PropTypes.bool,
  keyIconState: PropTypes.oneOf([
    'ACCESS_INFORMATION_LOCAL_KEYS_TYPE',
    'PROPERTY_ACCESS_LOCAL_KEYS_TYPE',
    'ACCESS_INFORMATION_REAL_ESTATE_KEYS_TYPE',
    'PROPERTY_ACCESS_REAL_ESTATE_KEYS_TYPE',
    'ACCESS_INFORMATION_OTHER_KEYS_TYPE',
    'PROPERTY_ACCESS_OTHER_KEYS_TYPE',
    'ACCESS_INFORMATION_KEYS_LOCATION_KEYS_TYPE',
    'PROPERTY_ACCESS_DETAILS_KEYS_TYPE',
    false,
  ]),
  formattedAllowedAtDate: PropTypes.string.isRequired,
  formattedCreatedAtDate: PropTypes.string.isRequired,
  formattedOrderAddress: PropTypes.string.isRequired,
  forwardedRef: PropTypes.object.isRequired,
  isMapSelected: PropTypes.bool,
  onMapOrderClick: PropTypes.func,
};

OrderCardComponent.defaultProps = {
  schedule: null,
  insideTimelineStyle: null,
  inTimeline: false,
  lockIconState: false,
  keyIconState: false,
  isMapSelected: false,
  onMapOrderClick: undefined,
};

export default injectIntl(OrderCardComponent);
